/**
 * Local catalogue search. No keystroke touches the network.
 *
 * Shops run on mobile data and phone hotspots. A request per keystroke makes the app unusable
 * before it makes it slow, so the whole catalogue is held in memory and searched here. Budget is
 * 200 ms at 3,000 variants; the measured figure is in `search.bench.ts`.
 *
 * Three passes, cheapest first, and the later ones only run when the earlier ones came up short:
 *
 *  1. **Barcode** — exact, via a map. A scanner is not typing and must not be ranked.
 *  2. **Token prefix and substring** — one linear pass over a pre-normalised index.
 *  3. **Fuzzy** — bounded edit distance, only for tokens of four characters or more, and only
 *     when pass 2 found fewer than five results. Running it always would triple the cost to fix
 *     a typo nobody made.
 */

import { isPresent, type CatalogueVariant } from '@pos/shared';

export type SearchHit = {
  readonly variant: CatalogueVariant;
  readonly score: number;
  /** What matched, so the UI can say "matched IMEI" rather than leaving the user guessing. */
  readonly reason: 'barcode' | 'sku' | 'name' | 'fuzzy';
};

type Entry = {
  readonly variant: CatalogueVariant;
  /** Everything searchable, lowercased and space-separated. */
  readonly haystack: string;
  readonly tokens: readonly string[];
  readonly sku: string;
};

export type CatalogueIndex = {
  readonly entries: readonly Entry[];
  readonly byBarcode: ReadonlyMap<string, CatalogueVariant>;
  readonly size: number;
};

const normalise = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Built once per catalogue load. ~3,000 entries costs roughly 25 ms. */
export function buildIndex(variants: readonly CatalogueVariant[]): CatalogueIndex {
  const entries: Entry[] = [];
  const byBarcode = new Map<string, CatalogueVariant>();

  for (const variant of variants) {
    if (!variant.isActive) continue;

    if (isPresent(variant.barcode) && variant.barcode.length > 0) {
      byBarcode.set(variant.barcode.trim(), variant);
    }

    // Attribute values are included so "128 black" finds the variant without the salesperson
    // knowing the product's exact display name.
    const parts = [
      variant.displayName,
      variant.productName,
      variant.brand ?? '',
      variant.sku,
      ...Object.values(variant.attributes),
    ];

    const haystack = normalise(parts.join(' '));

    entries.push({
      variant,
      haystack,
      tokens: haystack.split(' ').filter((token) => token.length > 0),
      sku: normalise(variant.sku),
    });
  }

  return { entries, byBarcode, size: entries.length };
}

export type SearchOptions = {
  readonly limit?: number | undefined;
  /** Hide variants with no stock. Off by default — negative stock is permitted by design. */
  readonly inStockOnly?: boolean | undefined;
};

export function search(
  index: CatalogueIndex,
  rawQuery: string,
  options: SearchOptions = {},
): readonly SearchHit[] {
  const limit = options.limit ?? 30;
  const trimmed = rawQuery.trim();

  if (trimmed.length === 0) return [];

  // A scanner's output is exact and complete. Short-circuiting here means a scan is one map
  // lookup rather than a ranked search that might put the wrong handset first.
  const scanned = index.byBarcode.get(trimmed);
  if (scanned) return [{ variant: scanned, score: 1000, reason: 'barcode' }];

  const query = normalise(trimmed);
  if (query.length === 0) return [];

  const terms = query.split(' ').filter((term) => term.length > 0);
  const hits: SearchHit[] = [];

  for (const entry of index.entries) {
    if (options.inStockOnly === true && entry.variant.quantity <= 0) continue;

    const score = scoreExact(entry, terms, query);
    if (score > 0) hits.push({ variant: entry.variant, score, reason: reasonFor(entry, query) });
  }

  // Pass 3. Only when the cheap passes came up short and the query is long enough for a typo to
  // be a plausible explanation rather than the user still typing.
  if (hits.length < 5 && query.length >= 4) {
    const found = new Set(hits.map((hit) => hit.variant.id));

    for (const entry of index.entries) {
      if (found.has(entry.variant.id)) continue;
      if (options.inStockOnly === true && entry.variant.quantity <= 0) continue;

      const score = scoreFuzzy(entry, terms);
      if (score > 0) hits.push({ variant: entry.variant, score, reason: 'fuzzy' });
    }
  }

  hits.sort((a, b) => b.score - a.score || a.variant.displayName.localeCompare(b.variant.displayName));
  return hits.slice(0, limit);
}

function reasonFor(entry: Entry, query: string): SearchHit['reason'] {
  return entry.sku.includes(query) ? 'sku' : 'name';
}

/**
 * Every term must match something, which is what makes a two-word query narrow rather than
 * widen. Position matters: a match at the start of the name is what the user meant.
 */
function scoreExact(entry: Entry, terms: readonly string[], query: string): number {
  let total = 0;

  for (const term of terms) {
    let best = 0;

    if (entry.sku === term) best = 200;
    else if (entry.sku.startsWith(term)) best = 120;
    else {
      for (const token of entry.tokens) {
        if (token === term) best = Math.max(best, 100);
        else if (token.startsWith(term)) best = Math.max(best, 70);
        else if (token.includes(term)) best = Math.max(best, 30);
      }
    }

    if (best === 0) return 0;
    total += best;
  }

  // A whole-phrase hit near the front of the haystack outranks the same words scattered.
  const position = entry.haystack.indexOf(query);
  if (position === 0) total += 60;
  else if (position > 0) total += 25;

  // Stock is a tiebreaker, never a filter: negative stock is permitted by design and hiding it
  // would hide the problem it is meant to surface.
  if (entry.variant.quantity > 0) total += 5;

  return total;
}

function scoreFuzzy(entry: Entry, terms: readonly string[]): number {
  let total = 0;

  for (const term of terms) {
    // A short term is not fuzzy-matchable — at three characters, an edit budget of one matches
    // most of the alphabet. It still has to match *something*, or "infnix hot" would score on
    // the misspelling alone and return every Infinix in the shop. So it falls back to an exact
    // check rather than failing the whole entry, which is what it used to do.
    if (term.length < 4) {
      const matched = entry.tokens.some((token) => token.startsWith(term));
      if (!matched) return 0;
      total += 20;
      continue;
    }

    const budget = term.length >= 7 ? 2 : 1;
    let best = 0;

    for (const token of entry.tokens) {
      // Length gap alone exceeds the budget — no need to compute the matrix.
      if (Math.abs(token.length - term.length) > budget) continue;

      const distance = boundedDistance(token, term, budget);
      if (distance <= budget) best = Math.max(best, 40 - distance * 12);
    }

    if (best === 0) return 0;
    total += best;
  }

  return total;
}

/**
 * Levenshtein with an early exit once every cell in a row exceeds the budget. Two rolling rows
 * rather than a full matrix: at these string lengths allocation dominates the arithmetic.
 */
function boundedDistance(a: string, b: string, budget: number): number {
  const rows = a.length;
  const cols = b.length;

  let previous = new Array<number>(cols + 1);
  let current = new Array<number>(cols + 1);

  for (let j = 0; j <= cols; j++) previous[j] = j;

  for (let i = 1; i <= rows; i++) {
    current[0] = i;
    let rowBest = current[0];

    for (let j = 1; j <= cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1]! + 1, previous[j]! + 1, previous[j - 1]! + cost);
      rowBest = Math.min(rowBest, current[j]!);
    }

    if (rowBest > budget) return budget + 1;

    [previous, current] = [current, previous];
  }

  return previous[cols]!;
}
